// Copyright (c) Meta Platforms, Inc. and affiliates.

/**
 * @file globalIconRegistry.tsx
 * @input None (pure module-level state)
 * @output Exports registerIcons, getIconRegistry, getIcon, resetIcons, IconName, IconRegistry
 * @position Global icon registry; works in both server and client environments
 *
 * This module has NO 'use client' directive — it's importable from RSC.
 * All components use getIcon() to resolve icons from this global registry.
 */

import { defaultIcons } from "./defaultIcons.js";

// =============================================================================
// Types
// =============================================================================

/**
 * Semantic icon names used internally by Astryx components.
 *
 * These represent the functional purpose of each icon, not a specific
 * visual representation. Themes provide the actual icon components.
 */
// SYNC: packages/cli/docs/icons.doc.mjs — update USAGE_HINTS when adding names

/**
 * Icon registry mapping semantic names to React nodes.
 */

// =============================================================================
// Global Registry
// =============================================================================

let globalRegistry = {};

/**
 * Register icons at the module level. Works in both server and client
 * environments. Call once at app initialization (e.g. root layout).
 *
 * Icons registered here are available to all components — including
 * server-rendered ones that can't access React Context.
 *
 * @example
 * ```
 * import { registerIcons } from '@astryxdesign/core';
 * import { brandIcons } from './brand-icons';
 * registerIcons(brandIcons);
 * ```
 */
export function registerIcons(icons) {
  globalRegistry = {
    ...globalRegistry,
    ...icons
  };
}

/**
 * Get a snapshot of the full icon registry, with registered icons overriding
 * built-in defaults.
 *
 * Works in both server and client environments. Useful for tooling that needs
 * to derive valid semantic icon-name options from the same registry Icon
 * resolves against.
 */
export function getIconRegistry() {
  const registry = {
    ...defaultIcons
  };
  for (const name of Object.keys(globalRegistry)) {
    registry[name] = globalRegistry[name] ?? defaultIcons[name];
  }
  return registry;
}

/**
 * Get an icon by name from the global registry, falling back to defaults.
 *
 * Works in both server and client environments.
 * Falls back to built-in default icons when no override is registered.
 */
export function getIcon(name) {
  return globalRegistry[name] ?? defaultIcons[name];
}

/**
 * Reset the global registry. For testing only.
 * @internal
 */
export function resetIcons() {
  globalRegistry = {};
}